import { useRef } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader.jsx";
import StayCard from "./StayCard.jsx";
import TopBannerCarousel from "./TopBannerCarousel.jsx";

const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    className="h-4 w-4 mx-auto"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    className="h-4 w-4 mx-auto"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const isInternalLink = (href) => String(href || "").startsWith("/");
export default function StayRow({
  id,
  eyebrow,
  title,
  subtitle,
  actionLabel = "View all",
  actionLink = "/listings",
  topBanners = [],
  cards = [],
}) {
  const railRef = useRef(null);
  const visibleCards = Array.isArray(cards) ? cards : [];
  const showArrows = visibleCards.length > 4;

  const scrollByStep = (direction) => {
    const el = railRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-card-width]");
    const cardWidth = firstCard
      ? firstCard.getBoundingClientRect().width
      : 320;
    el.scrollBy({ left: direction * (cardWidth + 16), behavior: "smooth" });
  };

  return (
    <section id={id} className="py-10 md:py-12">
      <div className="landing-container">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          action={
            isInternalLink(actionLink) ? (
              <Link
                to={actionLink}
                className="rounded-full border border-[rgba(201,161,91,0.35)] bg-white px-4 py-2 text-[10px] font-medium uppercase tracking-[0.28em] text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
              >
                {actionLabel}
              </Link>
            ) : (
              <a
                href={actionLink}
                className="rounded-full border border-[rgba(201,161,91,0.35)] bg-white px-4 py-2 text-[10px] font-medium uppercase tracking-[0.28em] text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
              >
                {actionLabel}
              </a>
            )
          }
        />

        {Array.isArray(topBanners) && topBanners.length ? (
          <div className="mt-4">
            <TopBannerCarousel
              slides={topBanners}
              fitWholeImage
              fallbackCtaText={actionLabel}
              fallbackCtaLink={actionLink}
            />
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          {showArrows ? (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollByStep(-1)}
                className="h-9 w-9 rounded-full border border-[var(--border)] bg-white text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollByStep(1)}
                className="h-9 w-9 rounded-full border border-[var(--border)] bg-white text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
              >
                <ChevronRight />
              </button>
            </div>
          ) : null}
          <div
            ref={railRef}
            className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory no-scrollbar"
          >
            {visibleCards.map((card) => (
              <div
                key={card.id}
                data-card-width
                className="min-w-[238px] sm:min-w-[252px] lg:min-w-[266px] xl:min-w-[278px] snap-start"
              >
                <StayCard stay={card} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
